import { useCallback, useEffect, useRef, useState, useMemo } from 'react';

import TreeCollection from '@hh.ru/magritte-ui-tree-selector/collection/treeCollection';
import { AdditionalDefault } from '@hh.ru/magritte-ui-tree-selector/collection/types';

export const areEqualArrays = (arr1: string[], arr2: string[]): boolean => {
    if (arr1.length !== arr2.length) {
        return false;
    }
    const sortArr2 = arr2.slice().sort();
    return arr1
        .slice()
        .sort()
        .every((item, index) => item === sortArr2[index]);
};

export const calculateInitialExpanded = <Additional extends AdditionalDefault>(
    initialExpanded: string[],
    selected: string[],
    expandTreeOnSelected: boolean,
    collection: TreeCollection<Additional>
): string[] => {
    let expanded: string[] = initialExpanded.slice();
    if (expandTreeOnSelected) {
        expanded = selected.reduce<string[]>((acc, id) => {
            return acc.concat(collection.getParentIds(id));
        }, expanded);
    }
    return [...new Set(expanded)];
};

interface UseExpandedHookProps<Additional extends AdditionalDefault> {
    initialValue: string[];
    onExpand?: (expanded: string[]) => void;
    expandTreeOnSelected: boolean;
    selected: string[];
    collection: TreeCollection<Additional>;
}

interface UseExpandedHookReturn {
    expanded: string[];
    setExpanded: (expanded: string[]) => void;
    handleExpansion: (id: string) => void;
    handleResetExpanded: () => void;
}
interface UseExpandedHook {
    <Additional extends AdditionalDefault>(props: UseExpandedHookProps<Additional>): UseExpandedHookReturn;
}

export const useExpanded: UseExpandedHook = ({
    initialValue,
    selected,
    expandTreeOnSelected,
    collection,
    onExpand,
}) => {
    const calculatedInitialValue = useMemo(
        () => calculateInitialExpanded(initialValue, selected, expandTreeOnSelected, collection),
        // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
        []
    );
    const [expanded, setExpanded] = useState(calculatedInitialValue);
    const expandedRef = useRef(calculatedInitialValue);
    const selectedRef = useRef(selected);
    selectedRef.current = selected;
    const handlerRef = useRef(onExpand);

    const handleSetExpanded = useCallback((updatedExpanded: string[]) => {
        handlerRef.current?.(updatedExpanded.slice());

        if (!areEqualArrays(expandedRef.current, updatedExpanded)) {
            setExpanded(updatedExpanded);
        }
    }, []);

    const handleExpansion = useCallback(
        (id: string): void => {
            let updatedExpanded;
            if (expandedRef.current.includes(id)) {
                updatedExpanded = expandedRef.current.filter((itemId) => itemId !== id);
            } else {
                updatedExpanded = expandedRef.current.slice();
                updatedExpanded.push(id);
            }
            handleSetExpanded(updatedExpanded);
        },
        [handleSetExpanded]
    );

    const handleResetExpanded = useCallback(() => {
        const calculatedResetValue = calculateInitialExpanded(
            initialValue,
            selectedRef.current,
            expandTreeOnSelected,
            collection
        );
        if (!areEqualArrays(expandedRef.current, calculatedResetValue)) {
            setExpanded(calculatedResetValue);
        }
        // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        handlerRef.current = onExpand;
    }, [onExpand]);

    useEffect(() => {
        expandedRef.current = expanded;
    }, [expanded]);

    return { expanded, setExpanded: handleSetExpanded, handleExpansion, handleResetExpanded };
};
